import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReportMenuItem } from './ReportMenuItem';
import { ReportPickerMenuProps } from './ReportPickerMenu.props';
import { SavedReport } from './ReportTabs.props';

const matchesSearch = (name: string, normalizedSearch: string) =>
  normalizedSearch === '' || name.toLowerCase().includes(normalizedSearch);

// In catalogue mode, show the search field only once there are enough reports
// for it to earn its space.
const SEARCH_THRESHOLD = 7;

/**
 * The tab strip's `+` menu, in one of two modes.
 *
 * **Recents** (`recentCodes` given) — what the user last opened. Typing in the
 * search box also surfaces the page's other reports under a second heading, so
 * the menu stays a fast path to a known report without becoming a dead end when
 * the one you want isn't recent. Used on pages that also have a Reports button.
 *
 * **Catalogue** (no `recentCodes`) — every report for the page, grouped into My
 * and Shared. Pages without a Reports button keep this: switching them to
 * recents would leave a user with no recents no way to reach their reports.
 */
export const ReportPickerMenu: React.FC<ReportPickerMenuProps> = ({
  anchorEl,
  open,
  reports,
  recentCodes,
  onClose,
  onPin,
  onSetDefault,
  onRemoveDefault,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isRecentsMode = recentCodes != null;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Recency order, not the report list's order. Codes whose report has since
  // been deleted or unshared are dropped rather than rendered as dead entries.
  const recentReports = useMemo(() => {
    if (!recentCodes) return [];
    const byCode = new Map(reports.map((r) => [r.code, r]));
    // Deduped: the codes come from localStorage, which nothing stops a user (or a
    // half-written older entry) from repeating, and a repeat would render the
    // same report twice under a duplicate React key.
    return [...new Set(recentCodes)]
      .map((code) => byCode.get(code))
      .filter((r): r is SavedReport => r != null);
  }, [reports, recentCodes]);

  // In recents mode the box searches the whole page catalogue, so it earns its
  // place as soon as there is anything to find — even with no recents yet.
  const showSearch = isRecentsMode ? reports.length > 0 : reports.length >= SEARCH_THRESHOLD;
  const normalizedSearch = showSearch ? search.trim().toLowerCase() : '';

  const sections = useMemo(() => {
    if (isRecentsMode) {
      const recentCodeSet = new Set(recentReports.map((r) => r.code));
      return [
        {
          key: 'recent',
          label: 'Recent Reports',
          items: recentReports.filter((r) => matchesSearch(r.name, normalizedSearch)),
        },
        {
          // Only while searching: unprompted, this menu is a recents list, and
          // padding it with everything else would make it the catalogue.
          key: 'other',
          label: 'Other Reports',
          items:
            normalizedSearch === ''
              ? []
              : reports.filter(
                  (r) => !recentCodeSet.has(r.code) && matchesSearch(r.name, normalizedSearch)
                ),
        },
      ];
    }
    return [
      {
        key: 'my',
        label: 'My Reports',
        items: reports.filter((r) => r.type === 'my' && matchesSearch(r.name, normalizedSearch)),
      },
      {
        key: 'shared',
        label: 'Shared Reports',
        items: reports.filter(
          (r) => r.type === 'shared' && matchesSearch(r.name, normalizedSearch)
        ),
      },
    ];
  }, [isRecentsMode, recentReports, reports, normalizedSearch]);

  const matchCount = sections.reduce((sum, s) => sum + s.items.length, 0);
  const firstMatch = sections.flatMap((s) => s.items)[0];

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && firstMatch) {
        e.preventDefault();
        onPin(firstMatch.code);
      }
    },
    [firstMatch, onPin]
  );

  const stopMenuKeyPropagation = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') return;
    e.stopPropagation();
  }, []);

  const emptyMessage =
    reports.length === 0
      ? 'No saved reports for this page yet.'
      : normalizedSearch !== ''
        ? 'No matching reports'
        : isRecentsMode
          ? 'No recent reports yet. Search above, or browse them all from the Reports button.'
          : 'No matching reports';

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- with search visible, focus the input; without, fall back to the Menu's default first-item focus so keyboard nav works
      autoFocus={!showSearch}
      slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 440 } } }}
    >
      {showSearch && (
        <Box
          onKeyDown={stopMenuKeyPropagation}
          sx={{
            px: 1,
            py: 0.5,
            position: 'sticky',
            top: 0,
            bgcolor: theme.palette.background.paper,
            zIndex: 1,
          }}
        >
          <TextField
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the search field is the menu's entry point; users opening this menu want to type immediately
            autoFocus
            fullWidth
            size="small"
            value={search}
            inputRef={searchInputRef}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={isRecentsMode ? 'Search recent reports' : 'Search reports'}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: search !== '' && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      // Keeps focus in the field so the user can keep typing,
                      // and stops the click reaching the menu item beneath.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearch('');
                        searchInputRef.current?.focus();
                      }}
                      sx={{ p: 0.25, color: 'text.secondary' }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
              htmlInput: {
                'aria-label': isRecentsMode ? 'Search recent reports' : 'Search reports',
              },
            }}
          />
        </Box>
      )}

      {sections.flatMap((section) =>
        section.items.length === 0
          ? []
          : [
              <ListSubheader key={section.key} sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
                {section.label}
              </ListSubheader>,
              ...section.items.map((r) => (
                <ReportMenuItem
                  key={r.code}
                  report={r}
                  onPin={onPin}
                  onSetDefault={onSetDefault}
                  onRemoveDefault={onRemoveDefault}
                />
              )),
            ]
      )}

      {/* Always say something when there is nothing to list — an empty menu
          with no explanation reads as a broken control. */}
      {matchCount === 0 && (
        <Box sx={{ px: 2, py: 1.5, maxWidth: 260 }} role="status" aria-live="polite">
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      )}
    </Menu>
  );
};
